/**
 * Lane <-> preview linking (W2-02; DESIGN.md §4.6, ARCHITECTURE.md §7.4) through the editor store's `hover`:
 *  - the lane hovers a point (`lanePoint`): a marker on that line at that X, using the line's pose at the playhead,
 *    shows a beginner where a click in the lane puts a note;
 *  - the lane hovers a note (`note`): its quads are outlined here;
 *  - hovering a note in the preview sets `hover` to that note, so the lane outlines it too.
 * Clicking a note to select it is selection.ts' job (same pick).
 */

import type { AppServices } from '../../../app/registry/types';
import { Mat2 } from '../../../core/math/mat2d';
import type { NoteId } from '../../../core/model/types';
import type { PreviewOverlayProvider } from '../../../render/types';
import { m } from '../preview.messages';
import { EXT_LANE_LINK, laneLinkOf, readExt } from '../prefs';
import { RPE_X_PER_WORLD } from './gizmoMath';
import { dp, drawLabel, frameInfo, noteQuadPx, pathQuad, quadsOfNote } from './shared';
import { overlayTheme } from './theme';

export function createLaneLinkOverlay(services: AppServices): PreviewOverlayProvider {
  /** The note this provider put into `hover` (so it only clears its own hover). */
  let ownHover: NoteId | null = null;
  const enabled = (): boolean => readExt(services.settings, EXT_LANE_LINK, laneLinkOf);
  return {
    id: 'preview.laneLink',
    order: 70,
    draw(c) {
      const hover = services.editor.getState().hover;
      if (hover === null || !enabled()) return;
      const theme = overlayTheme();
      const ctx = c.ctx;
      if (hover.kind === 'note') {
        ctx.save();
        ctx.strokeStyle = theme.hover;
        ctx.lineWidth = dp(c, 2);
        ctx.setLineDash([dp(c, 3), dp(c, 2)]);
        for (const k of quadsOfNote(c.list.notes, hover.note)) {
          pathQuad(ctx, noteQuadPx(c, c.list.notes, k));
          ctx.stroke();
        }
        ctx.restore();
        return;
      }
      const s = frameInfo(c).states.get(hover.line);
      if (s === undefined) return;
      // The point on the line at lane X (line-local x = X / 675), and the line's normal on screen.
      const [wx, wy] = Mat2.apply(s.m, hover.x / RPE_X_PER_WORLD, 0);
      const [nx, ny] = Mat2.apply(s.m, hover.x / RPE_X_PER_WORLD, 0.06);
      const [px, py] = c.worldToPx(wx, wy);
      const [qx, qy] = c.worldToPx(nx, ny);
      const len = Math.hypot(qx - px, qy - py) || 1;
      const ux = (qx - px) / len;
      const uy = (qy - py) / len;
      const h = dp(c, 16);
      ctx.save();
      ctx.strokeStyle = theme.hover;
      ctx.lineWidth = dp(c, 2);
      ctx.beginPath();
      ctx.moveTo(px - ux * h, py - uy * h);
      ctx.lineTo(px + ux * h, py + uy * h);
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(px, py, dp(c, 4), 0, Math.PI * 2);
      ctx.fillStyle = theme.hover;
      ctx.fill();
      ctx.restore();
      drawLabel(c, services.i18n.t(m.laneX, { x: Math.round(hover.x) }), px + dp(c, 8), py + dp(c, 8), {
        fg: theme.text,
        bg: theme.surface,
        border: theme.hover,
        size: 10,
      });
    },
    onPointerMove(c, e) {
      if (e.buttons !== 0) return;
      const editor = services.editor.getState();
      if (!enabled()) {
        if (ownHover !== null && editor.hover?.kind === 'note' && editor.hover.note === ownHover) editor.setHover(null);
        ownHover = null;
        return;
      }
      const [wx, wy] = c.pxToWorld(e.px, e.py);
      const note = c.engine.pickNote(c.list, wx, wy);
      const hover = editor.hover;
      if (note !== null) {
        if (hover?.kind !== 'note' || hover.note !== note) editor.setHover({ kind: 'note', note });
        ownHover = note;
        return;
      }
      if (ownHover !== null && hover?.kind === 'note' && hover.note === ownHover) editor.setHover(null);
      ownHover = null;
    },
  };
}
